// ─────────────────────────────────────────────────────────────────────────
// Cuadrilla — tablero de los agentes tmux del servidor.
//
// v1 (25-ago) mostraba estado + últimas líneas de la terminal de cada agente.
// v2 (28-ago) agrega lo que hacía falta para no tener que entrar por SSH:
//   · el ÚLTIMO INFORME de cada uno (título, cuándo y un extracto),
//   · su BACKLOG: qué tiene en cola y cuál es el siguiente,
//   · la SALUD de pm2, arriba de todo, porque si la app está caída eso es lo
//     primero que hay que ver,
//   · accesos rápidos a las pantallas que uno abre después de mirar esto.
//
// Sigue siendo SOLO LECTURA. Para destrabar un agente pegado se usa la sesión
// del celular o se le pide al Claude del PC — un tablero que además opera es un
// tablero que puede tumbar la app desde el navegador.
//
// La terminal de cada agente pasó a ser un detalle plegable: es lo más largo de
// la tarjeta y lo que menos se mira. Lo que se mira es qué hizo y qué sigue.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useContext } from "react";
import PanelConsultas from "./PanelConsultas";
import { Navigate, Link } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";

const ESTADOS = {
  trabajando:           { label: "Trabajando",            color: "#2ecc71", icon: "⚙️" },
  esperando_aprobacion: { label: "Esperando aprobación",  color: "#f39c12", icon: "✋" },
  en_espera:            { label: "En espera",             color: "#95a5a6", icon: "💤" },
  caida:                { label: "Sesión caída",          color: "#e74c3c", icon: "🔴" },
};

/**
 * Los estados que reporta el latido, con su color.
 *
 * `draft_sin_enviar` y `pide_permiso` son los dos que importan: un agente que escribió
 * algo y no lo mandó, o que está esperando un permiso, está DETENIDO aunque su sesión
 * esté viva. Ésos van en ámbar y con aviso a los 15 minutos, porque nadie los va a
 * destrabar si no se ven.
 */
const LATIDO_ESTADOS = {
  trabajando:       { texto: "trabajando",      color: "#2ecc71" },
  draft_sin_enviar: { texto: "escribió y no envió", color: "#f39c12" },
  pide_permiso:     { texto: "pide permiso",    color: "#f39c12" },
  ocioso:           { texto: "ocioso",          color: "#8a8a8a" },
  desconocido:      { texto: "sin latido",      color: "#8a8a8a" },
};

const hace = (fecha) => {
  if (!fecha) return "";
  const minutos = Math.round((Date.now() - new Date(fecha).getTime()) / 60000);
  if (minutos < 1) return "recién";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return `hace ${Math.round(horas / 24)} d`;
};

/** Salud de pm2. Va arriba: si la app está caída, es lo primero que hay que ver. */
const SaludPm2 = ({ pm2 }) => {
  if (!pm2) return null;
  if (!pm2.ok) {
    return (
      <div style={{ ...caja, borderColor: "#f39c1255", marginBottom: 14 }}>
        <div style={{ ...titulo, color: "#f39c12" }}>pm2</div>
        <div style={{ fontSize: 12, color: "#bbb" }}>{pm2.error}</div>
      </div>
    );
  }
  const caidos = pm2.caidos || [];
  return (
    <div
      style={{
        ...caja,
        borderColor: caidos.length ? "#e74c3c55" : "#2ecc7133",
        marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ ...titulo, color: caidos.length ? "#e74c3c" : "#2ecc71", marginBottom: 0 }}>
          pm2
        </div>
        <span style={{ fontSize: 12, color: caidos.length ? "#e74c3c" : "#888" }}>
          {caidos.length
            ? `${caidos.length} proceso(s) fuera de línea: ${caidos.join(", ")}`
            : `${pm2.procesos.length} proceso(s), todos en línea`}
        </span>
      </div>
      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse", color: "#c8ced4" }}>
          <thead>
            <tr style={{ color: "#777", textTransform: "uppercase", fontSize: 10 }}>
              <th style={celdaIzq}>Proceso</th>
              <th style={celdaIzq}>Estado</th>
              <th style={celdaDer}>CPU</th>
              <th style={celdaDer}>Memoria</th>
              <th style={celdaDer}>Arriba</th>
              <th style={celdaDer}>Reinicios</th>
            </tr>
          </thead>
          <tbody>
            {pm2.procesos.map((p) => (
              <tr key={p.nombre} style={{ borderTop: "1px solid #232323" }}>
                <td style={celdaIzq}>{p.nombre}</td>
                <td style={{ ...celdaIzq, color: p.estado === "online" ? "#2ecc71" : "#e74c3c" }}>
                  {p.estado}
                </td>
                <td style={celdaDer}>{p.cpu == null ? "—" : `${p.cpu}%`}</td>
                <td style={celdaDer}>{p.memoriaMb == null ? "—" : `${p.memoriaMb} MB`}</td>
                <td style={celdaDer}>
                  {p.minutosArriba == null
                    ? "—"
                    : p.minutosArriba < 90
                      ? `${p.minutosArriba} min`
                      : `${Math.round(p.minutosArriba / 60)} h`}
                </td>
                {/* Los reinicios INESTABLES se destacan: un proceso que vuelve a
                    levantarse solo está fallando aunque figure "online". */}
                <td style={{ ...celdaDer, color: p.reiniciosInestables > 0 ? "#f39c12" : undefined }}>
                  {p.reinicios}
                  {p.reiniciosInestables > 0 ? ` (${p.reiniciosInestables} inestables)` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const caja = {
  border: "1px solid #2a2a2a",
  borderRadius: 10,
  background: "#141414",
  padding: "10px 14px",
};
const titulo = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".07em",
  marginBottom: 8,
};
const celdaIzq = { textAlign: "left", padding: "5px 8px", whiteSpace: "nowrap" };
const celdaDer = { textAlign: "right", padding: "5px 8px", whiteSpace: "nowrap" };

const Cuadrilla = () => {
  useTitle("Cuadrilla IA");
  const authContext = useContext(AuthContext);
  const puedeVerCuadrilla = authContext?.can?.("ver_cuadrilla") || false;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Auditoría 28-ago: si el backend responde 403, se corta el polling — antes seguía
  // pegando cada 30 s a un endpoint que nunca iba a responder.
  const [detenido, setDetenido] = useState(false);
  const [terminalAbierta, setTerminalAbierta] = useState({});

  const cargar = useCallback(async () => {
    try {
      const res = await backend.get("cuadrilla");
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      if (e?.response?.status === 403) setDetenido(true);
    }
  }, []);

  useEffect(() => {
    if (!puedeVerCuadrilla || detenido) return;
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [cargar, puedeVerCuadrilla, detenido]);

  // Pantalla solo admin (patrón Auditoria.js) — después de los hooks.
  if (!puedeVerCuadrilla) return <Navigate to="/dashboard" replace />;

  return (
    <div className="cuad-wrap" style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      {/* flexWrap: en teléfono la hora de la foto baja de línea sin cortar el título */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <h2 className="mv-screen-title" style={{ margin: 0 }}>🤖 Cuadrilla IA</h2>
        <span style={{ fontSize: 12, color: "#888" }}>
          {data?.ts
            ? `foto: ${new Date(data.ts).toLocaleTimeString("es-CL")} · se refresca cada 30 s`
            : "cargando…"}
        </span>
      </div>
      <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
        Agentes trabajando 24/7 en el servidor. Si uno queda "esperando aprobación", se destraba desde
        la sesión del server en la app del celular o pidiéndoselo a Claude — el vigía además avisa por
        Ygg. Este tablero es de solo lectura. Para leer lo que producen (informes, hallazgos, guiones,
        actas), está la <Link to="/biblioteca">📚 Biblioteca</Link>.
      </p>

      {error && <div style={{ color: "#e74c3c", margin: "12px 0" }}>{error}</div>}
      {data?.error && <div style={{ color: "#f39c12", margin: "12px 0" }}>{data.error}</div>}

      <SaludPm2 pm2={data?.pm2} />

      {/* Accesos rápidos: adónde se va uno después de mirar el tablero. */}
      {data?.accesos?.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {data.accesos.map((a) => (
            <Link
              key={a.ruta}
              to={a.ruta}
              title={a.detalle}
              style={{
                fontSize: 12,
                color: "#c8ced4",
                textDecoration: "none",
                border: "1px solid #2a2a2a",
                borderRadius: 6,
                padding: "4px 10px",
                background: "#141414",
              }}
            >
              {a.label}
            </Link>
          ))}
        </div>
      )}

      {/* min(430px, 100%): en móvil la tarjeta no rebalsa el viewport */}
      <div
        className="cuad-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(430px, 100%), 1fr))",
          gap: 14,
        }}
      >
        {(data?.agents || []).map((a) => {
          const e = ESTADOS[a.estado] || ESTADOS.en_espera;
          const abierta = !!terminalAbierta[a.name];
          return (
            <div
              key={a.name}
              className="cuad-card"
              style={{ border: `1px solid ${e.color}55`, borderRadius: 10, background: "#141414", overflow: "hidden" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #2a2a2a" }}>
                <span style={{ fontSize: 18 }}>{e.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, textTransform: "capitalize" }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.rol}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: e.color, whiteSpace: "nowrap" }}>{e.label}</span>
              </div>

              {/* v3 — el LATIDO: en qué está y hace cuánto que no se mueve. El estado del
                  cron dice si la sesión existe; esto dice si adentro pasa algo. */}
              {a.latido && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px",
                              borderBottom: "1px solid #2a2a2a", flexWrap: "wrap" }}>
                  {(() => {
                    const L = LATIDO_ESTADOS[a.latido.estado] || LATIDO_ESTADOS.desconocido;
                    const pegado = a.latido.minSinCambio > 15 && a.latido.estado !== "ocioso";
                    return (
                      <>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 9,
                                       background: `${L.color}22`, color: L.color, border: `1px solid ${L.color}55` }}>
                          {L.texto}
                        </span>
                        <span style={{ fontSize: 11, color: pegado ? "#f39c12" : "#8a8a8a" }}>
                          {a.latido.minSinCambio === 0
                            ? "moviéndose ahora"
                            : `${a.latido.minSinCambio} min sin cambio`}
                          {pegado ? " ⚠" : ""}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 11, color: "#8a8a8a" }}>
                          {a.latido.abiertos} en cola · {a.latido.tachados} hechos
                        </span>
                      </>
                    );
                  })()}
                  {a.latido.siguiente && (
                    <div style={{ fontSize: 10, color: "#8a8a8a", width: "100%",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      sigue: {a.latido.siguiente}
                    </div>
                  )}
                </div>
              )}

              {/* Último informe */}
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #2a2a2a" }}>
                <div style={{ ...titulo, color: "#7f8c8d", marginBottom: 4 }}>Último informe</div>
                {a.informe ? (
                  <>
                    <div style={{ fontSize: 12.5, color: "#dfe4e8", fontWeight: 600 }}>
                      {a.informe.titulo}
                    </div>
                    <div style={{ fontSize: 11, color: "#777", marginBottom: 4 }}>
                      {hace(a.informe.modificadoEn)} · {a.informe.ruta}
                    </div>
                    {a.informe.extracto && (
                      <div style={{ fontSize: 11.5, color: "#9aa4ad", lineHeight: 1.5 }}>
                        {a.informe.extracto}…
                      </div>
                    )}
                    {a.informe.error && (
                      <div style={{ fontSize: 11, color: "#f39c12" }}>
                        no se pudo leer: {a.informe.error}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#777" }}>Sin informes.</div>
                )}
              </div>

              {/* Backlog */}
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #2a2a2a" }}>
                <div style={{ ...titulo, color: "#7f8c8d", marginBottom: 4 }}>
                  Backlog
                  {a.backlog && !a.backlog.error && (
                    <span style={{ color: "#666", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                      {" "}· {a.backlog.pendientes.length} pendiente(s)
                      {a.backlog.hechos > 0 ? ` · ${a.backlog.hechos} hecho(s)` : ""}
                    </span>
                  )}
                </div>
                {!a.backlog || a.backlog.error ? (
                  <div style={{ fontSize: 12, color: "#777" }}>Sin cola.</div>
                ) : a.backlog.pendientes.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#2ecc71" }}>Al día: no queda nada en cola.</div>
                ) : (
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: "#9aa4ad", lineHeight: 1.5 }}>
                    {a.backlog.pendientes.slice(0, 5).map((p, i) => (
                      // El primero es el que va a tomar cuando quede libre: se destaca.
                      <li key={p} style={i === 0 ? { color: "#dfe4e8" } : undefined}>
                        {p}
                      </li>
                    ))}
                    {a.backlog.pendientes.length > 5 && (
                      <li style={{ listStyle: "none", color: "#666" }}>
                        …y {a.backlog.pendientes.length - 5} más
                      </li>
                    )}
                  </ol>
                )}
              </div>

              {/* v3 — configuración del agente. Es lo que uno pregunta cuando algo anda
                  raro: quién es, dónde trabaja, qué cola lee. */}
              {a.configuracion && (
                <div style={{ padding: "8px 14px", borderBottom: "1px solid #2a2a2a",
                              fontSize: 10.5, color: "#7f8c8d", display: "flex",
                              gap: 12, flexWrap: "wrap" }}>
                  {a.configuracion.rol && <span>👤 {a.configuracion.rol}</span>}
                  {a.configuracion.cwd && <span style={{ fontFamily: "monospace" }}>📁 {a.configuracion.cwd}</span>}
                  <span style={{ fontFamily: "monospace" }}>🗒 BACKLOG/{a.configuracion.backlogArchivo}</span>
                  {a.configuracion.cron && <span>⏱ {a.configuracion.cron}</span>}
                </div>
              )}

              {/* v3 — el canal para hablarle. */}
              <PanelConsultas agente={a.name} consultas={data?.consultas || []} onEnviada={cargar} />

              {/* La terminal, plegada: es lo más largo y lo que menos se mira. */}
              <button
                type="button"
                onClick={() => setTerminalAbierta((p) => ({ ...p, [a.name]: !p[a.name] }))}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#7f8c8d",
                  fontSize: 11,
                  padding: "8px 14px",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                {abierta ? "▾" : "▸"} Terminal
              </button>
              {abierta && (
                <pre
                  className="cuad-pre"
                  style={{
                    margin: 0,
                    padding: "10px 14px",
                    fontSize: 10.5,
                    lineHeight: 1.45,
                    color: "#b8c0c8",
                    background: "#0d0d0d",
                    maxHeight: 260,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {a.pane || "(sin salida)"}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {/* Colas de agentes sin sesión viva. Sin esto, una cola alimentada para un agente
          caído no aparecería en ninguna parte del tablero. */}
      {data?.colasHuerfanas?.length > 0 && (
        <div style={{ ...caja, marginTop: 14, borderColor: "#f39c1255" }}>
          <div style={{ ...titulo, color: "#f39c12" }}>Colas sin agente en sesión</div>
          {data.colasHuerfanas.map((c) => (
            <div key={c.nombre} style={{ fontSize: 12, color: "#bbb", marginBottom: 4 }}>
              <strong style={{ textTransform: "capitalize" }}>{c.nombre}</strong>:{" "}
              {c.backlog.pendientes.length} pendiente(s) — {c.backlog.siguiente}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Cuadrilla;
